
import {defer as observableDefer, concat as observableConcat,  Observable, Subscription ,  defer ,  of } from 'rxjs';
import { Component, OnInit, OnDestroy } from "@angular/core";

import { LoaderService } from "../services/loader.service";
import { ObservableLoader } from "../models/loader.model";
import { finalize, catchError, retry, concat, tap } from "rxjs/operators";

// Fallback announcement for loads that don't pass explicit text.
const DEFAULT_ANNOUNCEMENT = 'Page loading, please wait';
const COMPLETE_ANNOUNCEMENT = 'Loading complete';
// aria-live regions are only re-announced by screen readers when their content changes,
// so we periodically toggle an invisible marker to force re-announcement while loading is up.
const REANNOUNCE_INTERVAL_MS = 10000;

@Component({
    selector: "loader",
    templateUrl: "./loader.component.html",
    styleUrls: ["./loader.component.scss"],
    standalone: false
})

export class LoaderComponent implements OnInit, OnDestroy {
    text: string;
    showLoader: boolean = false;

    // Single live region owning both the "loading" and "loading complete" announcements. It
    // has to be the one persistent region for this widget - the visual spinner (static-loader)
    // mounts/unmounts with showLoader, so it can announce "loading" on insertion but can't stick
    // around to announce completion. A second, separately-mounted live region for "complete"
    // was tried and pulled back out: two competing live regions updating close together caused
    // screen readers to drop announcements for both. static-loader is told [announce]="false"
    // so this is the only region speaking for this widget.
    //
    // Starts 'idle' until the first load happens, so the region opens empty instead of
    // announcing "Loading complete" before anything has actually loaded.
    private _status: 'idle' | 'loading' | 'complete' = 'idle';
    private _announceTick: boolean = false;
    private _announceIntervalId: ReturnType<typeof setInterval> | undefined;

    get announceText(): string {
        if (this._status === 'idle') return '';

        const base = this._status === 'loading' ? (this.text || DEFAULT_ANNOUNCEMENT) : COMPLETE_ANNOUNCEMENT;
        return base + (this._announceTick ? '⁠' : '');
    }

    constructor(public loaderService: LoaderService) {
        this.loaderService.register(this);
    }

    ngOnInit() {
        this.updateLoader();
    }

    ngOnDestroy() {
        this.stopReannounceInterval();
    }

    updateLoader(): void {
        this.loaderService.getLoader().subscribe(result => {
            this.setShowLoader(result.showLoader);
            this.text = result.text;
        });
    }

    load(observables: ObservableLoader[]): Observable<any> {
        return this.runObservables(observables);
    }

    public runObservables(observables: ObservableLoader[]): Observable<any> {
        return defer(() => {
            this.setShowLoader(true);

            return observableConcat(
                ...(observables.map(x => observableDefer(() => {
                    this.text = x.text;
                    return x.observable
                })))
            ).pipe(
                retry(5),
                finalize(() => {
                    this.text = null;
                    this.setShowLoader(false);
                })
            );
        });
    }

    // Only fires on a genuine activate/deactivate edge - callers may invoke this many times
    // while already loading (e.g. multiple components each holding the loader open via
    // LoaderService's pending-count), and this must announce/toggle the interval only on the
    // first activation and the last deactivation, not on every intermediate call.
    private setShowLoader(value: boolean): void {
        const wasLoading = this.showLoader;
        this.showLoader = value;

        if (wasLoading === value) return;

        this._status = value ? 'loading' : 'complete';
        // Toggle an invisible marker so the message text changes on every state flip,
        // guaranteeing re-announcement even when the new text matches a prior announcement.
        this._announceTick = !this._announceTick;

        if (value) {
            this.startReannounceInterval();
        } else {
            // Stop reannouncing once complete - otherwise "Loading complete" keeps getting
            // re-announced every interval tick indefinitely after the loader is gone.
            this.stopReannounceInterval();
        }
    }

    private startReannounceInterval(): void {
        this.stopReannounceInterval();
        this._announceIntervalId = setInterval(() => {
            this._announceTick = !this._announceTick;
        }, REANNOUNCE_INTERVAL_MS);
    }

    private stopReannounceInterval(): void {
        if (this._announceIntervalId != null) {
            clearInterval(this._announceIntervalId);
            this._announceIntervalId = undefined;
        }
    }
}
